import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Umbrella, Plus, Eye, Share2, Link2, KeyRound, Copy, Check as CheckIcon, Lock } from "lucide-react";
import { theme, card, btnPrimary, emptyFrame, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { LoadingBlock } from "../components/Spinner";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import { AssuranceSnapshotView, AnswerPill, type InsurerQuestion, type PackSnapshot } from "../components/AssuranceSnapshotView";
import {
  Check, EmptyState, Field, ModalHeader, ModalOverlay, StatusPill, dangerBtn, fmtDateTime, fmtDay, input, secondaryBtn, textarea,
} from "./resilience/shared";

// Assurance packs — what insurers, brokers, underwriters and customers ask for, shared on the
// organisation's terms (backend/src/routes/assurancePacks.routes.ts, lib/assurancePack.ts).
// The organisation chooses the sections, previews exactly what the recipient will see, and
// publishes; a link opens the version it was made from, never live data. Every write needs
// org:admin.

type Answer = "yes" | "partial" | "no";
type Standing = "active" | "withdrawn" | "expired" | "locked";

interface PackShare {
  id: string; packVersion: number; recipientLabel: string; expiresAt: string; revokedAt: string | null;
  viewCount: number; lastViewedAt: string | null; failedAttempts: number; createdAt: string; standing: Standing;
}
interface Pack {
  id: string; displayId: number | null; title: string; audience: string; recipientLabel: string;
  insurancePolicyId: string | null; sections: string[]; introduction: string | null;
  attestations: Record<string, { answer: Answer; statement: string }> | null;
  status: "draft" | "published" | "withdrawn"; snapshotAt: string | null; version: number;
  createdAt: string; updatedAt: string; shares: PackShare[];
}
interface Policy { id: string; insurer: string; broker: string | null; status: string; expiresAt: string }
interface Vocabulary {
  audiences: Array<{ id: string; label: string }>;
  sections: Array<{ id: string; label: string; description: string }>;
  defaults: Record<string, string[]>;
}
interface PacksView { packs: Pack[]; insurancePolicies: Policy[]; vocabulary: Vocabulary }

/** Short names of the insurer questions (keys from backend lib/assurancePack.ts INSURER_QUESTION_KEYS). */
const QUESTION_SHORT: Record<string, string> = {
  mfa: "MFA for email and remote access", admin_mfa: "MFA on administrator accounts", edr: "EDR on all endpoints",
  backups: "Offline backups and a tested restore", patching: "Security updates within 14 days",
  kev: "Free of open known-exploited vulnerabilities", training: "Annual awareness training", phishing: "Phishing simulations",
  incident_plan: "Tested incident response plan", continuity: "Continuity plan with recovery objectives",
  eol: "Software and hardware in vendor support", suppliers: "Critical suppliers assessed",
  cyber_essentials: "Cyber Essentials or equivalent", policies: "Policies approved and acknowledged",
};

const ref = (p: { displayId: number | null }) => (p.displayId != null ? `AP-${String(p.displayId).padStart(3, "0")}` : "AP");

function Pill({ bg, color, children }: { bg: string; color: string; children: React.ReactNode }) {
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: bg, color, whiteSpace: "nowrap" }}>{children}</span>;
}

function PackStatus({ pack }: { pack: Pack }) {
  if (pack.status === "published") return <Pill bg={theme.color.successBg} color={theme.color.successText}>Published v{pack.version}</Pill>;
  if (pack.status === "withdrawn") return <StatusPill value="withdrawn" />;
  return <StatusPill value="draft" />;
}

function StandingPill({ standing }: { standing: Standing }) {
  if (standing === "locked") return <Pill bg={theme.color.warningBg} color={theme.color.warningText}>Locked</Pill>;
  return <StatusPill value={standing} />;
}

const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const errorText: React.CSSProperties = { ...text.hint, color: theme.color.dangerText, marginTop: 10 };

export function AssurancePacksPage() {
  const { user } = useCurrentUser();
  const can = (p: string) => !!user?.permissions?.includes(p);
  const canEdit = can("org:admin");

  const [data, setData] = useState<PacksView | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState<{ pack: Pack | null } | null>(null);
  const [previewing, setPreviewing] = useState<Pack | null>(null);
  const [sharing, setSharing] = useState<Pack | null>(null);
  const [viewingPublished, setViewingPublished] = useState<Pack | null>(null);
  // ?pack=<id> opens a pack's read-only view.
  const [params, setParams] = useSearchParams();
  const viewId = params.get("pack");
  const openPack = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("pack", id); else next.delete("pack");
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      setData(await api.get<PacksView>("/assurance-packs"));
    } catch (e) { setError(apiErrorMessage(e, "Could not load the assurance packs.")); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function withdrawPack(p: Pack) {
    if (!window.confirm(`Withdraw ${ref(p)}? Every link issued for it stops working at once.`)) return;
    setError(""); setNotice("");
    try { await api.post(`/assurance-packs/${p.id}/withdraw`); await load(); setNotice(`${ref(p)} withdrawn. Its links no longer open.`); }
    catch (e) { setError(apiErrorMessage(e, "Could not withdraw the pack.")); }
  }

  async function deletePack(p: Pack) {
    if (!window.confirm(`Delete ${ref(p)} "${p.title}"? Its links stop working and the pack cannot be recovered.`)) return;
    setError(""); setNotice("");
    try { await api.del(`/assurance-packs/${p.id}`); await load(); }
    catch (e) { setError(apiErrorMessage(e, "Could not delete the pack.")); }
  }

  async function withdrawShare(s: PackShare) {
    if (!window.confirm(`Withdraw the link for ${s.recipientLabel}? It stops working at once.`)) return;
    setError("");
    try { await api.del(`/assurance-packs/shares/${s.id}`); await load(); }
    catch (e) { setError(apiErrorMessage(e, "Could not withdraw the link.")); }
  }

  const audienceLabel = (id: string) => data?.vocabulary.audiences.find((a) => a.id === id)?.label ?? id;

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
            <Umbrella size={20} /> Assurance packs
          </h1>
          <p style={{ ...pageSubtitle, maxWidth: 760 }}>
            Share what insurers, brokers, underwriters and customers ask for. You choose the sections,
            see exactly what they will see, and the link opens that version only.
          </p>
        </div>
        {canEdit && data && (
          <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} onClick={() => setEditing({ pack: null })}>
            <Plus size={14} /> New pack
          </button>
        )}
      </div>

      {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 12 }}>{error}</div>}
      {notice && <div style={{ ...text.hint, color: theme.color.successText, marginBottom: 12 }}>{notice}</div>}

      {!data ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 16 }}>
          {data.packs.length === 0 ? (
            <div style={card}>
              <EmptyState
                title="No assurance packs yet"
                detail="A pack answers the questions an insurer or customer asks — MFA, backups, patching, training, incidents, continuity — from your own records, with the evidence beside each answer."
                action={canEdit ? <button type="button" style={btnPrimary} onClick={() => setEditing({ pack: null })}>Create the first pack</button> : undefined}
              />
            </div>
          ) : data.packs.map((p) => {
            const activeLinks = p.shares.filter((s) => s.standing === "active").length;
            return (
              <div key={p.id} onClick={() => openPack(p.id)} role="button" style={{ ...card, padding: "14px 18px", cursor: "pointer" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ ...text.ref, color: theme.color.faint, fontWeight: 600 }}>{ref(p)}</span>
                  <span style={text.itemTitle}>{p.title}</span>
                  <PackStatus pack={p} />
                </div>
                <div style={{ ...text.hint, marginTop: 4, lineHeight: 1.6 }}>
                  {audienceLabel(p.audience)} · for {p.recipientLabel} · {p.sections.length} section{p.sections.length === 1 ? "" : "s"}
                  {p.snapshotAt ? ` · published ${fmtDay(p.snapshotAt)}` : " · not published"}
                  {` · ${activeLinks} active link${activeLinks === 1 ? "" : "s"}`}
                </div>
              </div>
            );
          })}

          <div style={{ ...card, padding: 18, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <Lock size={16} color={theme.color.subtle} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ ...text.hint, lineHeight: 1.6 }}>
              <div style={{ ...text.itemTitle, marginBottom: 4 }}>What stays private</div>
              A pack carries aggregates only: counts, percentages and dates. No names, no incident
              narratives, no host names and no personal data. The recipient never sees live data —
              a link opens the version you published when you made it, and nothing changes under
              them until you publish again and send a new link.
            </div>
          </div>
        </div>
      )}

      {viewId && data && !editing && !previewing && !sharing && (() => {
        const p = data.packs.find((x) => x.id === viewId);
        return p ? (
          <PackView
            pack={p} view={data} canEdit={canEdit} audienceLabel={audienceLabel}
            onClose={() => openPack(null)}
            onEdit={() => setEditing({ pack: p })}
            onPreview={() => setPreviewing(p)}
            onViewPublished={() => setViewingPublished(p)}
            onShare={() => setSharing(p)}
            onWithdraw={() => void withdrawPack(p)}
            onDelete={() => void deletePack(p)}
            onWithdrawShare={(sh) => void withdrawShare(sh)}
          />
        ) : null;
      })()}
      {editing && data && (
        <PackForm
          pack={editing.pack}
          view={data}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
      {previewing && (
        <PreviewModal
          pack={previewing}
          canPublish={canEdit}
          onClose={() => setPreviewing(null)}
          onPublished={async (version) => {
            await load();
            setNotice(`${ref(previewing)} published as version ${version}. You can now share it.`);
            setPreviewing(null);
          }}
        />
      )}
      {viewingPublished && (
        <PublishedModal pack={viewingPublished} onClose={() => setViewingPublished(null)} />
      )}
      {sharing && (
        <ShareModal
          pack={sharing}
          onClose={() => setSharing(null)}
          onCreated={() => void load()}
        />
      )}
    </div>
  );
}

// ── Read-only view ───────────────────────────────────────────────────────────

const ANSWER_LABEL: Record<Answer, string> = { yes: "Yes", partial: "Partly", no: "No" };

const INSURANCE_AUDIENCES = new Set(["insurer", "broker", "underwriter"]);

/** What a pack needs before it goes out: who it is for, what it shows, a covering note, and for insurers the policy and the statements beside their questions. */
function packSteps(p: Pack): NextStepItem[] {
  if (p.status === "withdrawn") return [];
  const insurance = INSURANCE_AUDIENCES.has(p.audience);
  return [
    { done: !!p.recipientLabel?.trim(), label: "Name the recipient" },
    { done: p.sections.length > 0, label: "Choose the sections to share" },
    { done: !!p.introduction?.trim(), label: "Write a short introduction for the recipient" },
    ...(insurance ? [
      { done: !!p.insurancePolicyId, label: "Link the insurance policy it supports" },
      ...(p.sections.includes("controls") ? [{ done: Object.keys(p.attestations ?? {}).length > 0, label: "Add your statements beside the insurer questions", hint: "Shown beside the answers the records give, never instead of them." }] : []),
    ] : []),
    { done: p.status === "published", label: "Preview it, then publish" },
    ...(p.status === "published" ? [{ done: p.shares.some((s) => s.standing === "active"), label: "Share it with the recipient" }] : []),
  ];
}

function PackView({ pack: p, view, canEdit, audienceLabel, onClose, onEdit, onPreview, onViewPublished, onShare, onWithdraw, onDelete, onWithdrawShare }: {
  pack: Pack; view: PacksView; canEdit: boolean; audienceLabel: (id: string) => string;
  onClose: () => void; onEdit: () => void; onPreview: () => void; onViewPublished: () => void; onShare: () => void;
  onWithdraw: () => void; onDelete: () => void; onWithdrawShare: (s: PackShare) => void;
}) {
  const policy = view.insurancePolicies.find((x) => x.id === p.insurancePolicyId);
  const sectionLabel = (id: string) => view.vocabulary.sections.find((x) => x.id === id)?.label ?? id;
  const statements = Object.entries(p.attestations ?? {});
  const withdrawable = p.shares.filter((sh) => sh.standing !== "withdrawn");
  const [shareToWithdraw, setShareToWithdraw] = useState("");
  const published = p.status === "published";
  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={<span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{ref(p)} · {p.title} <PackStatus pack={p} /></span>} onClose={onClose} />
      <div style={{ padding: "8px 20px 20px" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          {p.snapshotAt && (
            <button type="button" style={secondaryBtn} onClick={onViewPublished}>View published version</button>
          )}
          <button type="button" style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 5 }} onClick={onPreview}><Eye size={13} /> Preview what the recipient sees</button>
          {canEdit && <EditButton onClick={onEdit} />}
        </div>
        <NextSteps items={packSteps(p)} action={canEdit ? <button type="button" style={secondaryBtn} onClick={onEdit}>Fill these in</button> : undefined} />
        <DetailSection title="Details">
          <DetailFields fields={[
            { label: "Audience", value: audienceLabel(p.audience) },
            { label: "Recipient", value: p.recipientLabel },
            { label: "Insurance policy", value: policy ? `${policy.insurer}${policy.broker ? ` via ${policy.broker}` : ""} · renews ${fmtDay(policy.expiresAt)}` : null },
            { label: "Published", value: p.snapshotAt ? `Version ${p.version}, ${fmtDay(p.snapshotAt)}` : "Not published" },
            { label: "Last changed", value: fmtDateTime(p.updatedAt) },
            { label: "Introduction", value: p.introduction, wide: true, multiline: true },
          ]} />
        </DetailSection>
        <DetailSection title="Sections">
          {p.sections.length === 0 ? <div style={{ ...text.body, color: theme.color.faint }}>—</div> : (
            <ul style={{ margin: 0, paddingLeft: 18, ...text.body, display: "grid", gap: 2 }}>
              {p.sections.map((id) => <li key={id}>{sectionLabel(id)}</li>)}
            </ul>
          )}
        </DetailSection>
        {statements.length > 0 && (
          <DetailSection title="Your statements beside the insurer questions">
            <div style={{ display: "grid", gap: 8 }}>
              {statements.map(([key, v]) => (
                <div key={key} style={{ ...text.body }}>
                  <span style={{ color: theme.color.muted }}>{QUESTION_SHORT[key] ?? key}: </span>
                  <span style={{ fontWeight: 600 }}>{ANSWER_LABEL[v.answer] ?? v.answer}</span>
                  <span style={{ color: theme.color.muted }}> · {v.statement}</span>
                </div>
              ))}
              <div style={text.hint}>Preview the pack to see each statement beside its question and evidenced answer.</div>
            </div>
          </DetailSection>
        )}
        <DetailSection title="Links">
          {p.shares.length === 0 ? (
            <div style={{ ...emptyFrame, ...text.body, color: theme.color.muted }}>No links issued yet.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={{ textAlign: "left", color: theme.color.subtle, fontSize: text.label.fontSize }}>
                  {["Recipient", "Version", "Created", "Expires", "Views", "Last viewed", "Standing"].map((h) => <th key={h} style={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {p.shares.map((sh) => (
                    <tr key={sh.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                      <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>{sh.recipientLabel}</td>
                      <td style={td}>v{sh.packVersion}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDay(sh.createdAt)}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDay(sh.expiresAt)}</td>
                      <td style={td}>{sh.viewCount}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{sh.lastViewedAt ? fmtDateTime(sh.lastViewedAt) : "not yet"}</td>
                      <td style={td}><StandingPill standing={sh.standing} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DetailSection>
        {canEdit && (
          <RecordActions note={published ? undefined : "Publish the pack from its preview before sharing it; a link always shows a published version."}>
            <button
              type="button"
              disabled={!published}
              style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 5, opacity: published ? 1 : 0.5, cursor: published ? "pointer" : "not-allowed" }}
              onClick={onShare}
            ><Share2 size={13} /> Share</button>
            {withdrawable.length > 0 && (
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <select value={shareToWithdraw} onChange={(e) => setShareToWithdraw(e.target.value)} style={{ ...input, width: "auto", maxWidth: 260 }} aria-label="Link to withdraw">
                  <option value="">Choose a link…</option>
                  {withdrawable.map((sh) => <option key={sh.id} value={sh.id}>{sh.recipientLabel} · v{sh.packVersion} · expires {fmtDay(sh.expiresAt)}</option>)}
                </select>
                <button type="button" style={{ ...dangerBtn, opacity: shareToWithdraw ? 1 : 0.5 }} disabled={!shareToWithdraw} onClick={() => {
                  const sh = withdrawable.find((x) => x.id === shareToWithdraw);
                  if (sh) { onWithdrawShare(sh); setShareToWithdraw(""); }
                }}>Withdraw link</button>
              </span>
            )}
            {p.status === "published" && <button type="button" style={dangerBtn} onClick={onWithdraw}>Withdraw pack</button>}
            <button type="button" style={dangerBtn} onClick={onDelete}>Delete pack</button>
          </RecordActions>
        )}
      </div>
    </ModalOverlay>
  );
}

// ── Create / edit ────────────────────────────────────────────────────────────

type AttestationDraft = Record<string, { answer: Answer | ""; statement: string }>;

function PackForm({ pack, view, onClose, onSaved }: { pack: Pack | null; view: PacksView; onClose: () => void; onSaved: () => Promise<void> }) {
  const { vocabulary, insurancePolicies } = view;
  const firstAudience = vocabulary.audiences[0]?.id ?? "insurer";
  const [title, setTitle] = useState(pack?.title ?? "");
  const [audience, setAudience] = useState(pack?.audience ?? firstAudience);
  const [recipientLabel, setRecipientLabel] = useState(pack?.recipientLabel ?? "");
  const [policyId, setPolicyId] = useState(pack?.insurancePolicyId ?? "");
  const [introduction, setIntroduction] = useState(pack?.introduction ?? "");
  const [sections, setSections] = useState<string[]>(pack?.sections ?? vocabulary.defaults[firstAudience] ?? []);
  const [attestations, setAttestations] = useState<AttestationDraft>(() => {
    const out: AttestationDraft = {};
    for (const [k, v] of Object.entries(pack?.attestations ?? {})) out[k] = { answer: v.answer, statement: v.statement };
    return out;
  });
  const [questions, setQuestions] = useState<InsurerQuestion[] | null>(null);
  const [questionsState, setQuestionsState] = useState<"idle" | "loading" | "unavailable" | "error">("idle");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // The question keys and wording come from the saved pack's insurer questions (answered as the
  // preview would), so the editor lists exactly what the recipient will see, with the evidenced
  // answer beside each.
  useEffect(() => {
    if (!pack) return;
    if (!pack.sections.includes("controls")) { setQuestionsState("unavailable"); return; }
    let cancelled = false;
    setQuestionsState("loading");
    api.get<{ questions: InsurerQuestion[] }>(`/assurance-packs/${pack.id}/questions`)
      .then((r) => {
        if (cancelled) return;
        setQuestions(r.questions);
        setQuestionsState("idle");
      })
      .catch(() => { if (!cancelled) setQuestionsState("error"); });
    return () => { cancelled = true; };
  }, [pack]);

  function changeAudience(a: string) {
    setAudience(a);
    setSections(vocabulary.defaults[a] ?? []);
  }

  const quickFills = Array.from(new Set(
    audience === "broker"
      ? insurancePolicies.map((p) => p.broker).filter((b): b is string => !!b)
      : audience === "insurer" || audience === "underwriter"
        ? insurancePolicies.map((p) => p.insurer)
        : [],
  ));

  function setAtt(key: string, patch: Partial<{ answer: Answer | ""; statement: string }>) {
    setAttestations((cur) => ({ ...cur, [key]: { answer: cur[key]?.answer ?? "", statement: cur[key]?.statement ?? "", ...patch } }));
  }

  async function save() {
    if (!title.trim() || !recipientLabel.trim()) { setError("Give the pack a title and say who it is for."); return; }
    const out: Record<string, { answer: Answer; statement: string }> = {};
    for (const [k, v] of Object.entries(attestations)) {
      const statement = v.statement.trim();
      if (!v.answer && !statement) continue;
      if (!v.answer || !statement) { setError("For each statement, choose an answer and write the statement — or leave both empty."); return; }
      out[k] = { answer: v.answer, statement };
    }
    const body: Record<string, unknown> = {
      title: title.trim(), audience, recipientLabel: recipientLabel.trim(),
      insurancePolicyId: policyId || null, sections, introduction: introduction.trim() || null,
    };
    // Only send statements when the editor was shown; otherwise the saved ones stay as they are.
    if (questions || !pack) body.attestations = out;
    setBusy(true); setError("");
    try {
      if (pack) await api.put(`/assurance-packs/${pack.id}`, body);
      else await api.post("/assurance-packs", body);
      await onSaved();
    } catch (e) { setError(apiErrorMessage(e, "Could not save the pack.")); } finally { setBusy(false); }
  }

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={pack ? `Edit ${ref(pack)}` : "New assurance pack"} onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
          <Field label="Title" span2>
            <input style={input} maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Cyber insurance renewal 2026" />
          </Field>
          <Field label="Audience" help="Changing it chooses that audience's usual sections; you can adjust them below.">
            <select style={input} value={audience} onChange={(e) => changeAudience(e.target.value)}>
              {vocabulary.audiences.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </Field>
          <Field label="Recipient" help="Who the pack is for. It is shown on the pack and in the audit trail.">
            <input style={input} maxLength={200} value={recipientLabel} onChange={(e) => setRecipientLabel(e.target.value)} placeholder="e.g. the insurer's underwriting team" />
          </Field>
          {quickFills.length > 0 && (
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", ...text.hint }}>
              From your insurance record:
              {quickFills.map((n) => (
                <button key={n} type="button" onClick={() => setRecipientLabel(n)} style={{ ...secondaryBtn, padding: "3px 10px" }}>{n}</button>
              ))}
            </div>
          )}
          <Field label="Insurance policy (optional)" span2 help="The policy the Current cover section describes. Left empty, the active policy on record is used.">
            <select style={input} value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
              <option value="">No specific policy</option>
              {insurancePolicies.map((p) => (
                <option key={p.id} value={p.id}>{p.insurer}{p.broker ? ` via ${p.broker}` : ""} · {p.status} · renews {fmtDay(p.expiresAt)}</option>
              ))}
            </select>
          </Field>
          <Field label="Introduction" span2 help="Optional. Shown at the top of the pack, in your own words.">
            <textarea style={{ ...textarea, minHeight: 80 }} maxLength={4000} value={introduction} onChange={(e) => setIntroduction(e.target.value)} />
          </Field>
        </div>

        <div>
          <div style={{ ...text.label, marginBottom: 8 }}>Sections</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
            {vocabulary.sections.map((s) => (
              <Check
                key={s.id}
                checked={sections.includes(s.id)}
                onChange={(on) => setSections((cur) => (on ? [...cur, s.id] : cur.filter((x) => x !== s.id)))}
                label={s.label}
                help={s.description}
              />
            ))}
          </div>
        </div>

        <div style={{ borderTop: "1px solid var(--c-border)", paddingTop: 14 }}>
          <div style={{ ...text.sectionTitle, margin: 0 }}>Insurer questions — your statements</div>
          <div style={{ ...text.hint, marginTop: 4 }}>
            Each question is answered from your records, with the evidence beside it. Where you know
            more than the platform can show, add your own answer and statement. It is shown as the
            organisation's statement beside the evidenced answer — never instead of it.
          </div>
          {!pack && <div style={{ ...text.hint, marginTop: 10 }}>Save the pack first, then open it again to add statements beside the questions.</div>}
          {pack && questionsState === "loading" && <div style={{ marginTop: 10 }}><LoadingBlock label="Loading the questions…" /></div>}
          {pack && questionsState === "unavailable" && <div style={{ ...text.hint, marginTop: 10 }}>Tick Security controls and save; the questions appear here when you open the pack again.</div>}
          {pack && questionsState === "error" && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 10 }}>Could not load the questions. Your saved statements are kept.</div>}
          {questions && (
            <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
              {questions.map((q) => {
                const a = attestations[q.key] ?? { answer: "", statement: "" };
                return (
                  <div key={q.key} style={{ ...card, padding: 12 }}>
                    <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div style={{ ...text.body, fontWeight: 600, flex: "1 1 280px" }}>{q.question}</div>
                      <AnswerPill answer={q.answer} />
                    </div>
                    <div style={{ ...text.hint, marginTop: 4 }}>{q.evidence}</div>
                    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <select style={{ ...input, width: 150, flex: "0 0 auto" }} value={a.answer} onChange={(e) => setAtt(q.key, { answer: e.target.value as Answer | "" })}>
                        <option value="">No statement</option>
                        <option value="yes">Yes</option>
                        <option value="partial">Partly</option>
                        <option value="no">No</option>
                      </select>
                      <textarea
                        style={{ ...textarea, flex: "1 1 260px", minHeight: 38 }}
                        rows={1}
                        maxLength={1000}
                        value={a.statement}
                        onChange={(e) => setAtt(q.key, { statement: e.target.value })}
                        placeholder="Your statement, e.g. what is in place that the platform cannot see"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {error && <div style={errorText}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void save()}>{pack ? "Save changes" : "Create pack"}</button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ── Preview and publish ──────────────────────────────────────────────────────

function PreviewModal({ pack, canPublish, onClose, onPublished }: { pack: Pack; canPublish: boolean; onClose: () => void; onPublished: (version: number) => Promise<void> }) {
  const [snapshot, setSnapshot] = useState<PackSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get<{ snapshot: PackSnapshot }>(`/assurance-packs/${pack.id}/preview`)
      .then((r) => { if (!cancelled) setSnapshot(r.snapshot); })
      .catch((e) => { if (!cancelled) setError(apiErrorMessage(e, "Could not build the preview.")); });
    return () => { cancelled = true; };
  }, [pack.id]);

  async function publish() {
    setBusy(true); setError("");
    try {
      const r = await api.post<{ pack: { version: number } }>(`/assurance-packs/${pack.id}/publish`);
      await onPublished(r.pack.version);
    } catch (e) { setError(apiErrorMessage(e, "Could not publish the pack.")); } finally { setBusy(false); }
  }

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader
        title={`Preview — ${ref(pack)}`}
        onClose={onClose}
        right={canPublish && snapshot ? (
          <button type="button" style={{ ...btnPrimary, padding: "6px 14px", opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void publish()}>Publish this version</button>
        ) : undefined}
      />
      <div style={{ padding: 20 }}>
        <div style={{ ...text.hint, marginBottom: 16 }}>
          This is exactly what the recipient will see, built from your records now. Publishing freezes
          it{pack.status === "published" ? ` as version ${pack.version + 1}; links already sent keep showing version ${pack.version}` : ""}.
        </div>
        {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 12 }}>{error}</div>}
        {!snapshot ? (error ? null : <LoadingBlock label="Building the preview…" />) : (
          <div style={{ ...card, padding: 20 }}>
            <AssuranceSnapshotView snapshot={snapshot} />
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}

// ── Published version ────────────────────────────────────────────────────────

/** The frozen snapshot links open (GET /assurance-packs/:id/published), as against the live preview. */
function PublishedModal({ pack, onClose }: { pack: Pack; onClose: () => void }) {
  const [published, setPublished] = useState<{ snapshot: PackSnapshot | null; snapshotAt: string | null; version: number } | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get<{ snapshot: PackSnapshot | null; snapshotAt: string | null; version: number }>(`/assurance-packs/${pack.id}/published`)
      .then((r) => { if (!cancelled) setPublished(r); })
      .catch((e) => { if (!cancelled) setError(apiErrorMessage(e, "Could not load the published version.")); });
    return () => { cancelled = true; };
  }, [pack.id]);

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={`Published version — ${ref(pack)}`} onClose={onClose} />
      <div style={{ padding: 20 }}>
        {published?.snapshotAt && (
          <div style={{ ...text.hint, marginBottom: 16 }}>
            Version {published.version}, frozen on {fmtDay(published.snapshotAt)}. Links made since then open this version{pack.status === "withdrawn" ? ", although the pack is withdrawn and they no longer answer" : ""}.
          </div>
        )}
        {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 12 }}>{error}</div>}
        {!published ? (error ? null : <LoadingBlock label="Loading the published version…" />) : !published.snapshot ? (
          <div style={{ ...emptyFrame, ...text.body, color: theme.color.muted }}>This pack has not been published yet.</div>
        ) : (
          <div style={{ ...card, padding: 20 }}>
            <AssuranceSnapshotView snapshot={published.snapshot} />
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}

// ── Share ────────────────────────────────────────────────────────────────────

function ShareModal({ pack, onClose, onCreated }: { pack: Pack; onClose: () => void; onCreated: () => void }) {
  const [recipientLabel, setRecipientLabel] = useState(pack.recipientLabel);
  const [days, setDays] = useState(30);
  const [sendForMe, setSendForMe] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [passKeyEmail, setPassKeyEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ url: string; passKey: string; emailed: boolean; recipientLabel: string } | null>(null);
  const [copied, setCopied] = useState<"" | "link" | "key">("");

  const sameAddress = sendForMe && linkEmail.trim() !== "" && linkEmail.trim().toLowerCase() === passKeyEmail.trim().toLowerCase();

  async function create() {
    if (!recipientLabel.trim()) { setError("Say who the link is for."); return; }
    if (!Number.isInteger(days) || days < 1 || days > 90) { setError("A link can stay open for 1 to 90 days."); return; }
    if (sendForMe && (!linkEmail.trim() || !passKeyEmail.trim())) { setError("Give both addresses, or untick the option and send them yourself."); return; }
    if (sameAddress) { setError("Use two different addresses, so one intercepted email is not enough to open the pack."); return; }
    setBusy(true); setError("");
    try {
      const r = await api.post<{ share: { recipientLabel: string }; url: string; passKey: string; emailed: boolean }>(`/assurance-packs/${pack.id}/shares`, {
        recipientLabel: recipientLabel.trim(), expiresInDays: days,
        ...(sendForMe ? { linkEmail: linkEmail.trim(), passKeyEmail: passKeyEmail.trim() } : {}),
      });
      setResult({ url: r.url, passKey: r.passKey, emailed: r.emailed, recipientLabel: r.share.recipientLabel });
      onCreated();
    } catch (e) { setError(apiErrorMessage(e, "Could not create the link.")); } finally { setBusy(false); }
  }

  function copy(what: "link" | "key", value: string) {
    navigator.clipboard?.writeText(value).then(() => setCopied(what)).catch(() => {});
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Share ${ref(pack)} v${pack.version}`} onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 14 }}>
        {!result ? (
          <>
            <div style={text.hint}>
              The link opens version {pack.version} as published on {fmtDay(pack.snapshotAt)}. It needs a
              pass key as well, sent separately, and ten wrong attempts lock it.
            </div>
            <Field label="Recipient">
              <input style={input} maxLength={200} value={recipientLabel} onChange={(e) => setRecipientLabel(e.target.value)} />
            </Field>
            <Field label="Link open for (days)" help="Between 1 and 90 days.">
              <input style={{ ...input, width: 120 }} type="number" min={1} max={90} value={days} onChange={(e) => setDays(Number(e.target.value))} />
            </Field>
            <Check
              checked={sendForMe}
              onChange={setSendForMe}
              label="Send it for me"
              help="The platform emails the link and the pass key itself, to two different addresses."
            />
            {sendForMe && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                <Field label="Send the link to">
                  <input style={input} type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} />
                </Field>
                <Field label="Send the pass key to">
                  <input style={input} type="email" value={passKeyEmail} onChange={(e) => setPassKeyEmail(e.target.value)} />
                </Field>
                <div style={{ gridColumn: "1 / -1", fontSize: text.meta.fontSize, color: sameAddress ? theme.color.dangerText : theme.color.faint }}>
                  The two addresses must differ, so one intercepted email is not enough to open the pack.
                </div>
              </div>
            )}
            {error && <div style={errorText}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
              <button type="button" style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void create()}>Create link</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: 12, background: "var(--c-panelAlt)", borderRadius: 3, border: `1px solid ${theme.color.border}` }}>
              <div style={{ ...text.itemTitle, marginBottom: 2 }}>Shown once — copy both now</div>
              <div style={{ ...text.hint, marginBottom: 10 }}>
                {result.emailed
                  ? `The platform has emailed the link and the pass key separately. Keep these only if you need them; neither can be shown again.`
                  : `Send the link to ${result.recipientLabel}, then the pass key in a second message. Neither half opens the pack on its own. Neither can be shown again — if the pass key is lost, withdraw this link and create another.`}
              </div>
              <CredentialRow label="Link — first message" icon={<Link2 size={13} color={theme.color.accent} />} value={result.url} copied={copied === "link"} onCopy={() => copy("link", result.url)} />
              <CredentialRow label="Pass key — second message" icon={<KeyRound size={13} color={theme.color.accent} />} value={result.passKey} copied={copied === "key"} onCopy={() => copy("key", result.passKey)} large />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" style={btnPrimary} onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

function CredentialRow({ label, icon, value, copied, onCopy, large }: {
  label: string; icon: React.ReactNode; value: string; copied: boolean; onCopy: () => void; large?: boolean;
}) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ ...text.label, display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
        {icon}{label}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <code style={{
          flex: 1, minWidth: 0, padding: "6px 8px", background: "var(--c-panel)", border: `1px solid ${theme.color.border}`,
          borderRadius: 3, ...text.mono, fontSize: large ? text.itemTitle.fontSize : text.mono.fontSize, letterSpacing: large ? "1px" : undefined,
          fontWeight: large ? 700 : 400, color: theme.color.heading, wordBreak: "break-all",
        }}>{value}</code>
        <button type="button" onClick={onCopy} style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 5 }}>
          {copied ? <CheckIcon size={13} /> : <Copy size={13} />}{copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
